import { Tabs } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Heart, List, MessageSquare, Star, Tag } from "react-feather";
import { useTranslation } from "react-i18next";
import { DOWNLOAD_PHOTOS } from "../../constants/album";
import { useGetAlbumApi } from "../../services/apis/album.api";
import { IDownloadAlbum, TDownloadAlbum, TImage } from "../../types/album.type";
import OptionsTxt from "./OptionsTxt";
import SummaryTxt from "./SummaryTxt";

const TOTAL_KEY = "total" as const;
type TDownloadTxtKey = TDownloadAlbum | typeof TOTAL_KEY;

const isDownloadTab = (key: TDownloadTxtKey): key is TDownloadAlbum =>
  key !== TOTAL_KEY;

const DownloadTxt = (props: IDownloadAlbum) => {
  const { id, status, totalPhotos, tags } = props;
  const { t } = useTranslation();
  const dfOption = Object.keys(status).find(
    (item) => status[item as TDownloadAlbum]
  ) as TDownloadAlbum | undefined;
  const hasSummaryData =
    status[DOWNLOAD_PHOTOS.LIKE] ||
    status[DOWNLOAD_PHOTOS.TAG] ||
    status[DOWNLOAD_PHOTOS.COMMENT];
  const initialTab: TDownloadTxtKey =
    dfOption ?? (hasSummaryData ? TOTAL_KEY : DOWNLOAD_PHOTOS.LIKE);
  const [value, setValue] = useState<TDownloadTxtKey>(initialTab);

  const filterOptions = useMemo(() => {
    if (!isDownloadTab(value)) return undefined;
    if (value === DOWNLOAD_PHOTOS.TAG) {
      return undefined;
    }
    return { [value]: 1 };
  }, [value]);

  const tagIdsForFilter = useMemo(() => {
    if (!isDownloadTab(value) || value !== DOWNLOAD_PHOTOS.TAG) {
      return undefined;
    }
    return tags?.map((item) => item.id);
  }, [value, tags]);

  const { data, isLoading, refetch } = useGetAlbumApi({
    perPage: totalPhotos,
    albumIdProps: id,
    filterParams: filterOptions,
    tagIds: tagIdsForFilter,
    enabled: isDownloadTab(value) && !!status[value],
  });

  // Fetch riêng biệt cho từng loại filter khi ở tab Total
  const likeQuery = useGetAlbumApi({
    perPage: totalPhotos,
    albumIdProps: id,
    filterParams: status[DOWNLOAD_PHOTOS.LIKE]
      ? ({ is_liked: 1 } as any)
      : undefined,
    enabled: value === TOTAL_KEY && status[DOWNLOAD_PHOTOS.LIKE],
  });

  const commentQuery = useGetAlbumApi({
    perPage: totalPhotos,
    albumIdProps: id,
    filterParams: status[DOWNLOAD_PHOTOS.COMMENT]
      ? ({ is_commented: 1 } as any)
      : undefined,
    enabled: value === TOTAL_KEY && status[DOWNLOAD_PHOTOS.COMMENT],
  });

  const tagQuery = useGetAlbumApi({
    perPage: totalPhotos,
    albumIdProps: id,
    tagIds:
      status[DOWNLOAD_PHOTOS.TAG] && tags?.length
        ? tags.map((item) => item.id)
        : undefined,
    enabled:
      value === TOTAL_KEY &&
      status[DOWNLOAD_PHOTOS.TAG] &&
      !!tags?.length,
  });

  useEffect(() => {
    if (isDownloadTab(value) && status[value]) {
      refetch();
    }
  }, [value, refetch, status]);

  useEffect(() => {
    if (value === TOTAL_KEY && hasSummaryData) {
      if (status[DOWNLOAD_PHOTOS.LIKE]) {
        likeQuery.refetch();
      }
      if (status[DOWNLOAD_PHOTOS.COMMENT]) {
        commentQuery.refetch();
      }
      if (status[DOWNLOAD_PHOTOS.TAG] && tags?.length) {
        tagQuery.refetch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, hasSummaryData, status, tags?.length]);

  const tabItems: Array<{ key: TDownloadTxtKey; icon: JSX.Element }> = [
    { key: DOWNLOAD_PHOTOS.LIKE, icon: <Heart className="w-4" /> },
    { key: DOWNLOAD_PHOTOS.TAG, icon: <Tag className="w-4" /> },
    { key: DOWNLOAD_PHOTOS.COMMENT, icon: <MessageSquare className="w-4" /> },
    { key: DOWNLOAD_PHOTOS.RECOMMEND, icon: <Star className="w-4" /> },
    { key: TOTAL_KEY, icon: <List className="w-4" /> },
  ];

  const currentImages =
    data?.pages[0]?.data?.data?.files?.data ?? ([] as TImage[]);
  const currentAlbumName =
    data?.pages[0]?.data?.data?.name ??
    likeQuery.data?.pages?.[0]?.data?.data?.name ??
    commentQuery.data?.pages?.[0]?.data?.data?.name ??
    tagQuery.data?.pages?.[0]?.data?.data?.name ??
    "";

  // Merge images từ các query, loại bỏ duplicate theo id
  const summaryImages = useMemo(() => {
    if (value !== TOTAL_KEY) return [] as TImage[];

    const imageMap = new Map<string, TImage>();

    // Merge từ like query
    likeQuery.data?.pages?.forEach((page) => {
      const files = page?.data?.data?.files?.data ?? [];
      files.forEach((file) => {
        if (file.id) imageMap.set(file.id, file);
      });
    });

    // Merge từ comment query
    commentQuery.data?.pages?.forEach((page) => {
      const files = page?.data?.data?.files?.data ?? [];
      files.forEach((file) => {
        if (file.id) imageMap.set(file.id, file);
      });
    });

    // Merge từ tag query
    tagQuery.data?.pages?.forEach((page) => {
      const files = page?.data?.data?.files?.data ?? [];
      files.forEach((file) => {
        if (file.id) imageMap.set(file.id, file);
      });
    });

    return Array.from(imageMap.values());
  }, [value, likeQuery.data, commentQuery.data, tagQuery.data]);

  const isLoadingTotal =
    likeQuery.isLoading ||
    commentQuery.isLoading ||
    tagQuery.isLoading ||
    likeQuery.isFetching ||
    commentQuery.isFetching ||
    tagQuery.isFetching;

  const summaryName =
    likeQuery.data?.pages?.[0]?.data?.data?.name ??
    commentQuery.data?.pages?.[0]?.data?.data?.name ??
    tagQuery.data?.pages?.[0]?.data?.data?.name ??
    currentAlbumName;

  return (
    <div className="flex flex-col">
      <Tabs
        activeKey={value}
        items={tabItems.map((item) => {
          if (item.key === TOTAL_KEY) {
            return {
              key: item.key,
              disabled: !hasSummaryData,
              label: (
                <div className="flex gap-1 items-center">
                  {item.icon}
                  {t("dashboard.albums.action.download.txt.total")}
                </div>
              ),
              children: (
                <SummaryTxt
                  images={summaryImages}
                  name={summaryName}
                  isLoading={isLoadingTotal}
                  hasSummaryData={hasSummaryData}
                />
              ),
            };
          }

          const tabKey = item.key as TDownloadAlbum;
          return {
            key: tabKey,
            disabled: isLoading || !status[tabKey],
            label: (
              <div className="flex gap-1 items-center">
                {item.icon}
                {t(`dashboard.albums.action.download.txt.${tabKey}`)}
              </div>
            ),
            children: (
              <OptionsTxt
                images={currentImages}
                name={currentAlbumName}
                isTag={tabKey === DOWNLOAD_PHOTOS.TAG}
                isComment={tabKey === DOWNLOAD_PHOTOS.COMMENT}
              />
            ),
          };
        })}
        onTabClick={(key) => {
          setValue(key as TDownloadTxtKey);
        }}
        className="flex justify-between"
      />
    </div>
  );
};

export default DownloadTxt;
